// Señales de seguridad al servidor (ADR 0004 §6, `POST /v1/capture/telemetry`).
// Nunca lanza y nunca lleva datos personales: la etiqueta de la cámara solo viaja como hash.
import type { CaptureClient } from './client.js';
import type { CaptureDevice, CaptureLocale, CaptureSdk, CaptureTelemetryKind } from './types.js';

/** Clases de señal que se envían aunque la telemetría general esté apagada. */
const SECURITY_KINDS: ReadonlySet<CaptureTelemetryKind> = new Set([
  'virtual_camera',
  'guard_failed',
]);

export interface TelemetryOptions {
  sdk: CaptureSdk;
  locale: CaptureLocale;
  /** `false` apaga `unsupported_browser` y `sdk_error`; las señales de seguridad siempre se envían. */
  enabled?: boolean;
}

/** Valores permitidos en `details` (sin objetos anidados ni textos largos). */
export type TelemetryDetails = Record<string, string | number | boolean>;

/**
 * Envío de telemetría de la captura.
 *
 * ```ts
 * const tel = new Telemetry(client, { sdk, locale: 'es' });
 * await tel.send('virtual_camera', { camera_label_hash }, { reason: 'virtual_camera' }); // nunca lanza
 * ```
 */
export class Telemetry {
  constructor(
    private readonly client: CaptureClient,
    private readonly opts: TelemetryOptions,
  ) {}

  /** Envía una señal. Devuelve `true` si el servidor la aceptó; nunca lanza. */
  async send(
    kind: CaptureTelemetryKind,
    device: Partial<CaptureDevice> = {},
    details?: TelemetryDetails,
  ): Promise<boolean> {
    if (this.opts.enabled === false && !SECURITY_KINDS.has(kind)) return false;
    try {
      await this.client.telemetry({
        kind,
        sdk: this.opts.sdk,
        device,
        locale: this.opts.locale,
        ...(details ? { details: sanitize(details) } : {}),
      });
      return true;
    } catch {
      return false;
    }
  }
}

/** Recorta claves a 64 y textos a 200 caracteres (límites del contrato). */
function sanitize(details: TelemetryDetails): TelemetryDetails {
  const out: TelemetryDetails = {};
  for (const [k, v] of Object.entries(details).slice(0, 32))
    out[k.slice(0, 64)] = typeof v === 'string' ? v.slice(0, 200) : v;
  return out;
}
